import React, { useState, useEffect } from 'react'
import { Redirect } from 'react-router'
import { ApolloError } from 'apollo-client'
import { useKey, useSessionStorage, useLocalStorage } from 'react-use'
import { GraphQLError } from 'graphql'
import * as Sentry from '@sentry/browser'

import { IS_EMBEDDED } from '@sketch/constants'
import { routes, EmbeddedErrorPage } from '@sketch/modules-common'
// Components
import {
  useForTablet,
  CenterContent,
  IsEmbedded,
  NotEmbedded,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'
import DetailHeader from 'modules/shares/components/DetailHeader'
import { EmbeddedDetailHeader } from 'modules/shares/components/EmbeddedDetailHeader'
import { CustomFontsMessage } from 'modules/shares/components/CustomFontsMessage'
import { DocumentSidebarLayoutExtraProps } from '../components/DocumentSidebarLayout'

// Local Components
import DetailSidebar from '../components/DetailSidebar'
import { useGetActiveSidebarTab } from '../components/ShareSidebarTabContext'

// Styles
import { PageContent } from './DetailView.styles'

// Types
import { LayerId, Pagination } from '../types'
import { ArtboardDetailInfoFragment, VersionFragment } from '@sketch/gql-types'
import { InspectorError } from 'modules/shares/Inspector/types'

// Hooks
import useGetInspectorData from './useGetInspectorData'
import { useSymbol } from '../Inspector/Symbol'

// Context
import { ArtboardContext, defaultLayoutSettings } from './ArtboardContext'
import { ComponentDescriptionContextProvider } from '../ComponentsWebView/context'

// Utils
import { ErrorHandler } from '@sketch/tracing'
import Inspector from '../Inspector/Sidebar/components/Inspector'
import DocumentMobileToolbar, {
  isMobileToolboxItem,
} from '../components/DocumentMobileToolbar'
import { usePlaceDraftAnnotation } from 'modules/annotations/hooks'
import { ShareWithoutVersion } from 'modules/versioning'

import { ArtboardCanvas } from './ArtboardCanvas'
import { useCanvasZoom } from 'components/WebRendererCanvas/hooks'
import { DetailSidebarPlaceholder } from '../components/DetailSidebar/DetailSidebar'

/**
 * TYPES
 */
export interface DetailViewProps {
  share: ShareWithoutVersion
  currentVersion: VersionFragment | undefined
  artboard: ArtboardDetailInfoFragment | undefined
  isViewingLatestVersion: boolean
  isPrototypePlayEnabled: boolean
  pagination: Pagination | undefined
  artboardIdAtLatestVersion: string | undefined
  error: ApolloError | undefined
  loading: boolean
  layoutProps: DocumentSidebarLayoutExtraProps
}

function hasGraphQLErrorCode(
  apolloError: ApolloError,
  errorCode: string
): GraphQLError | undefined {
  return apolloError.graphQLErrors.find(
    error => error.extensions?.code === errorCode
  )
}

/**
 * COMPONENT
 */
export const DetailView = ({
  share,
  currentVersion,
  artboard,
  isViewingLatestVersion,
  isPrototypePlayEnabled,
  pagination,
  artboardIdAtLatestVersion,
  error,
  loading,
  layoutProps,
}: DetailViewProps) => {
  const {
    HeaderPortal,
    SidebarRightPortal,
    useOverrideLayoutProps,
    isSidebarRightOpen,
    setSidebarLeftOpen,
    toggleSidebarRight,
  } = layoutProps

  const isTabletAndBigger = useForTablet()
  const activeSidebarTab = useGetActiveSidebarTab()

  // Override layout default values
  useOverrideLayoutProps({
    title: artboard?.name,
  })

  // Annotations hooks

  const [placeDraftAnnotation, setPlacingDraftAnnotation] =
    usePlaceDraftAnnotation() || []

  // Inspector Internal state
  const [hoveredLayerId, setHoveredLayerId] = useState<LayerId | undefined>()
  const [selectedLayerId, setSelectedLayerId] = useState<LayerId | undefined>()
  const [isGridEnabled, setIsGridEnabled] = useLocalStorage(
    'inspector_grid_enabled',
    false
  )
  const [isLayoutEnabled, setIsLayoutEnabled] = useLocalStorage(
    'inspector_layout_enabled',
    false
  )
  const [inspectorActive, setInspectorActive] = useSessionStorage<boolean>(
    'inspector-active',
    false
  )

  const isInspectorEnabled = activeSidebarTab === 'Inspect'

  // Manage inspector data
  const artboardId = artboard?.identifier
  const [
    loadInspectorData,
    { inspectorData, inspectorError, inspectorErrorReason, inspectorIsReady },
  ] = useGetInspectorData({
    artboard,
    // We generate the data on demand, poll every 4 seconds to see if it's ready
    pollInterval: 4000,
  })

  const inspectorDataErrors = inspectorData?.errors
    ? inspectorData.errors
        .map((error: InspectorError) => error.extensions.code)
        .join(', ')
    : null
  const inspectorDataErrorReasons = inspectorData?.errors
    ? inspectorData.errors
        .map((error: InspectorError) => error.extensions.reason)
        .join(', ')
    : null

  // Reset selected layers when the artboard change
  useEffect(() => {
    setSelectedLayerId(undefined)
    setHoveredLayerId(undefined)
  }, [artboardId])

  // Symbol data needed for both canvas (ArtboardView) and sidebar (DetailSidebar)
  const {
    symbolLayer,
    symbolData,
    symbolArtboard,
    foreignSymbolShareId,
    foreignVersionShortId,
    foreignSymbolShareName,
    foreignSymbolShareError,
  } = useSymbol({
    inspectorData,
    selectedLayerId,
    currentVersion,
  })

  useEffect(() => {
    // we want to load the inspector IF we have an artboard, the tab is selected or we need to load the grid or layout
    if (
      artboard &&
      artboardId &&
      (isInspectorEnabled || isGridEnabled || isLayoutEnabled)
    ) {
      loadInspectorData()
    }
  }, [
    artboard,
    loadInspectorData,
    inspectorActive,
    artboardId,
    setInspectorActive,
    isGridEnabled,
    isLayoutEnabled,
    isInspectorEnabled,
  ])

  // Inspector data object needed for both canvas (ArtboardView) and sidebar (DetailSidebar)
  const inspector = {
    selectedLayerId,
    hoveredLayerId,
    isDataReady: inspectorIsReady,
    data: inspectorData,
    isActive: isInspectorEnabled,
    onClickLayer: setSelectedLayerId,
    onHoverLayer: setHoveredLayerId,
    symbol: {
      symbolLayer,
      symbolData,
      symbolArtboard: symbolArtboard
        ? {
            ...symbolArtboard,
            // TODO: mark id fields of Artboard as non nullable
            // https://github.com/sketch-hq/Cloud/issues/4679
            permanentArtboardShortId: symbolArtboard.permanentArtboardShortId!,
          }
        : null,
      foreignSymbolShareId,
      foreignVersionShortId,
      foreignSymbolShareName,
      foreignSymbolShareError,
    },
  }

  // keyboard shortcuts
  useKey(
    'Escape',
    () => {
      if (isInspectorEnabled && selectedLayerId) {
        setSelectedLayerId(undefined)
      }
    },
    {},
    [selectedLayerId]
  )

  const { showToast } = useToast()
  useEffect(() => {
    if (error && !IS_EMBEDDED) {
      if (hasGraphQLErrorCode(error, 'NOT_FOUND')) {
        showToast('This artboard does not exist.', 'negative')
      } else if (!error.networkError) {
        showToast(
          'Oops, something went wrong while loading the artboard.',
          'negative'
        )
        Sentry.captureException(error)
        ErrorHandler.shouldNeverHappen(
          `Something went wrong while fetching getDetailViewArtboard query: '${error.message}'`
        )
      }
    }
  }, [error, showToast])

  const shouldRenderSidebarRight = !error && !IS_EMBEDDED
  const sidebarDataReady = !!artboard && !!share && !!currentVersion

  const [zoom, setZoom] = useCanvasZoom()

  const pageUUID = artboard?.page?.uuid
  const artboardUUID = artboard?.uuid

  const toggleGrid = () => setIsGridEnabled(!isGridEnabled)
  const toggleLayout = () => setIsLayoutEnabled(!isLayoutEnabled)

  const inspectorUI =
    artboard && isInspectorEnabled ? (
      <Inspector
        isPage={artboard.isPage}
        isSymbol={artboard.isSymbolMaster}
        ready={inspectorIsReady}
        error={inspectorDataErrors ?? inspectorError}
        errorReason={inspectorDataErrorReasons ?? inspectorErrorReason}
        inspector={inspector}
        artboardName={artboard.name}
        layer={
          selectedLayerId &&
          inspectorData &&
          inspectorData.layers[selectedLayerId as string]
        }
        shareIdentifier={share.identifier}
        currentVersion={currentVersion!}
        permanentArtboardShortId={artboard.permanentArtboardShortId!}
      />
    ) : null

  return (
    <CenterContent>
      <ArtboardContext.Provider
        value={{
          isInspectorEnabled,
          isGridEnabled: !!isGridEnabled,
          toggleGrid,
          isLayoutEnabled: !!isLayoutEnabled,
          toggleLayout,
          zoomFactor: zoom?.factor ?? 1,
          gridSize: inspectorData?.grid?.size ?? 20,
          thickGridTimes: inspectorData?.grid?.thickGridTimes ?? 10,
          layoutSettings: inspectorData?.layout ?? defaultLayoutSettings,
        }}
      >
        <ComponentDescriptionContextProvider>
          <HeaderPortal>
            <NotEmbedded>
              <DetailHeader
                share={share}
                artboard={artboard}
                artboardLatestVersion={artboardIdAtLatestVersion!}
                setSidebarLeftOpen={setSidebarLeftOpen}
                toggleSidebarRight={toggleSidebarRight}
                isSidebarRightOpen={isSidebarRightOpen}
                isViewingLatestVersion={isViewingLatestVersion}
                isPrototypePlayEnabled={isPrototypePlayEnabled}
                userCanOpenInApp={!!currentVersion?.document?.userCanOpenInApp}
                zoom={zoom}
                setZoom={setZoom}
              />
            </NotEmbedded>
            <IsEmbedded>
              {!error && !loading && (
                <EmbeddedDetailHeader
                  share={share}
                  artboard={artboard}
                  pagination={pagination}
                  currentZoom={zoom}
                  setZoom={setZoom}
                  showPrototypePlayButton={
                    (artboard &&
                      artboard.isFlowHome &&
                      isPrototypePlayEnabled) ||
                    false
                  }
                />
              )}
            </IsEmbedded>
          </HeaderPortal>
          <SidebarRightPortal>
            {shouldRenderSidebarRight &&
              (sidebarDataReady ? (
                <DetailSidebar
                  share={share}
                  artboard={artboard}
                  userCanInspect={share.userCanInspect}
                  toggleSidebar={toggleSidebarRight}
                  isViewingLatestRevision={isViewingLatestVersion}
                  inspectorUI={inspectorUI}
                />
              ) : (
                <DetailSidebarPlaceholder />
              ))}
          </SidebarRightPortal>

          <PageContent>
            {
              // <DismissibleCard /> will render a warning when there are
              // missing fonts in the artboard. If dismissed it wont render
              // again for any artboard in that particular document
            }
            <CustomFontsMessage
              userCanEdit={share.userCanEdit}
              shareIdentifier={share.identifier}
              hasMissingFonts={!!artboard?.hasMissingFonts}
            />
            {error ? (
              <>
                <NotEmbedded>
                  <Redirect
                    to={routes.SHARE_VIEW.create({
                      shareID: share.identifier,
                    })}
                  />
                </NotEmbedded>
                <IsEmbedded>
                  <EmbeddedErrorPage for="artboard" />
                </IsEmbedded>
              </>
            ) : (
              pageUUID &&
              artboardUUID && (
                <ArtboardCanvas
                  shareIdentifier={share.identifier}
                  pageUUID={pageUUID}
                  hasInspectorDataError={Boolean(
                    inspectorDataErrors || inspectorError
                  )}
                  inspectorDataIsReady={inspector.isDataReady}
                  permanentArtboardShortId={artboard?.permanentArtboardShortId}
                  inspector={inspector}
                  artboard={artboard}
                  currentVersion={currentVersion}
                  pagination={pagination}
                />
              )
            )}
          </PageContent>
          {!IS_EMBEDDED && !isTabletAndBigger && (
            <DocumentMobileToolbar
              zoom={zoom}
              setZoom={setZoom}
              gridAndLayout={
                isInspectorEnabled
                  ? {
                      isGridEnabled: Boolean(isGridEnabled),
                      toggleGrid,
                      isLayoutEnabled: Boolean(isLayoutEnabled),
                      toggleLayout,
                    }
                  : undefined
              }
              isPrototypePlayEnabled={isPrototypePlayEnabled}
              shareIdentifier={share.identifier}
              items={[
                'zoom',
                artboard?.isFlowHome && 'prototype',
                'add-annotation',
              ].filter(isMobileToolboxItem)}
              artboardUUID={artboard?.uuid || ''}
              isDraftAnnotationEnabled={placeDraftAnnotation}
              setPlaceDraftAnnotation={setPlacingDraftAnnotation}
            />
          )}
        </ComponentDescriptionContextProvider>
      </ArtboardContext.Provider>
    </CenterContent>
  )
}
